// React component

import { Link } from "react-router-dom";
import Wrapper from "../global/wrapper";
import { Button } from "../ui/button";
import MobileMenu from "./mobile-menu";
import FeaturesDropdown from "./features-dropdown";
import { ThemeToggle } from "../ui/theme-toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useLocation } from "react-router-dom";
import useFeatureFlag from "@/hooks/useFeatureFlag";

const Navbar = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const isRealtimePage = pathname?.includes("/features/real-time");

  // Add pricing page feature flag
  const isPricingPageEnabled = useFeatureFlag("pricingPage");

  // Apply special styling for the real-time page
  const headerClasses = isRealtimePage
    ? "sticky top-0 w-full h-20 sm:h-28 bg-transparent backdrop-blur-md z-50 border-b border-pink-500/10"
    : "sticky top-0 w-full h-20 sm:h-28 bg-background/80 backdrop-blur-sm z-50";

  // Special styling for links on real-time page
  const linkClasses = isRealtimePage
    ? "text-lg font-medium link relative z-10"
    : "text-lg font-medium link";

  return (
    <header className={headerClasses} data-oid="831rs.x">
      {isRealtimePage && (
        <div
          className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-transparent overflow-hidden"
          data-oid="d.uxold"
        >
          {/* Decorative elements that complement the real-time background */}
          <div
            className="absolute top-0 left-1/4 w-[200px] h-[100px] bg-pink-500/5 rounded-full blur-3xl"
            data-oid="5pt:47."
          ></div>
          <div
            className="absolute top-0 right-1/4 w-[150px] h-[80px] bg-blue-500/5 rounded-full blur-3xl"
            data-oid="8ye77xg"
          ></div>

          {/* Subtle animated line */}
          <div
            className="absolute bottom-0 w-full h-[1px] bg-gradient-to-r from-transparent via-pink-500/20 to-transparent"
            data-oid="c_qytf9"
          ></div>
        </div>
      )}

      <Wrapper className="h-full" data-oid="wj605xx">
        <div
          className="flex items-center justify-between h-full"
          data-oid="ysk7qma"
        >
          <div
            className="flex items-end pb-2 overflow-hidden lg:overflow-visible sm:-ml-4 md:-ml-6"
            data-oid="6:ks1nl"
          >
            <Link to="/" className="flex items-center" data-oid="hav2o-z">
              <div
                className="relative w-[150px] sm:w-[200px] h-20 sm:h-28 flex items-end justify-start"
                data-oid="-zh:f-e"
              >
                <img
                  src="/images/log_title.svg"
                  alt="Blyrha Logo"
                  className="w-full h-full object-contain scale-[2.5] transform translate-x-12 sm:translate-x-16 lg:translate-x-24 translate-y-8 sm:translate-y-12 brightness-0 invert"
                  data-oid="sjdg1pe"
                />
              </div>
            </Link>
          </div>

          <div
            className="hidden lg:flex items-center gap-6 -ml-60"
            data-oid="iasmqx-"
          >
            <FeaturesDropdown data-oid="d:5bxuh" />
            {isPricingPageEnabled && (
              <Link to="/pricing" className={linkClasses} data-oid="i2.f4pm">
                Pricing
              </Link>
            )}
            <Link to="/blog" className={linkClasses} data-oid="_e_10pu">
              Blog
            </Link>
            <DropdownMenu data-oid="79pe_hl">
              <DropdownMenuTrigger asChild data-oid="x7vc:jm">
                <button className={linkClasses} data-oid="6vkizue">
                  More
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className={
                  isRealtimePage
                    ? "bg-background/90 backdrop-blur-xl border border-pink-500/10"
                    : ""
                }
                data-oid="kwegnj7"
              >
                <DropdownMenuItem asChild data-oid="aztk5:e">
                  <Link to="/faq" data-oid="2_d:kxk">
                    FAQ
                  </Link>
                </DropdownMenuItem>
                {/**
                 * <DropdownMenuItem asChild data-oid="rgw4:zf">
                 *   <Link to="/casestudy" data-oid="wjb-o0.">
                 *     Case Studies
                 *   </Link>
                 * </DropdownMenuItem>
                 * <DropdownMenuItem asChild data-oid="lidzmet">
                 *   <Link to="/resources" data-oid="ww9elke">
                 *     Resources
                 *   </Link>
                 * </DropdownMenuItem>
                 */}
                <DropdownMenuItem asChild data-oid="y01yr.k">
                  <Link to="/about" data-oid="s:.w:wm">
                    About
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-oid="fct5k69">
                  <Link to="/contact" data-oid="x2jow2d">
                    Contact
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3" data-oid="ac2rh2o">
            {/* Theme Toggle */}
            <ThemeToggle data-oid="js-qb98" />

            {/* Desktop Get Started button */}
            <Link to="/demo" className="hidden lg:block" data-oid="i39udwc">
              <Button
                variant={isRealtimePage ? "outline" : "default"}
                className={
                  isRealtimePage
                    ? "border-pink-500/20 bg-background/30 backdrop-blur-md hover:bg-pink-500/10"
                    : ""
                }
                data-oid="hs_thez"
              >
                Get Started
              </Button>
            </Link>

            {/* Mobile Get Started button */}
            <Link to="/demo" className="lg:hidden" data-oid="mf9gimo">
              <Button
                variant={isRealtimePage ? "outline" : "default"}
                size="sm"
                className={`text-xs px-3 py-1 h-8 ${isRealtimePage ? "border-pink-500/20 bg-background/30 backdrop-blur-md hover:bg-pink-500/10" : ""}`}
                data-oid="_vp9_ul"
              >
                Get Started
              </Button>
            </Link>

            <MobileMenu data-oid="99wbu19" />
          </div>
        </div>
      </Wrapper>
    </header>
  );
};

export default Navbar;
